import { format, parseISO } from 'date-fns';

export const AD_HOC_SCHEDULED_SEQUENCE_ID = 'ad_hoc_scheduled';

const STABLE_STATUSES = new Set([
  'draft_ready',
  'needs_review',
  'failed',
  'sent',
  'scheduled',
  'error',
  'replied',
  'bounced',
  'cancelled',
  'manual_completion',
]);

export function resolveOutboxStepStatus(draft) {
  const raw = draft?.status ?? draft?.generation_status ?? '';
  return String(raw || '').toLowerCase().trim();
}

function hasOutboxMessageBody(draft) {
  if (!draft) return false;
  const html = draft.body_html ?? draft.bodyHtml ?? draft.body ?? draft.html ?? '';
  const text = String(html).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  if (text.length > 0) return true;
  return String(draft.subject ?? '').trim().length > 0;
}

function isAdHocOutboxDraft(draft) {
  if (!draft) return false;
  const id = String(draft.stepId ?? draft.step_id ?? draft.id ?? '');
  const source = String(draft.source ?? '').toLowerCase();
  return Boolean(
    draft.is_temporary ||
    id.startsWith('draft-') ||
    draft.is_ad_hoc ||
    draft.isAdHoc ||
    source.includes('ad_hoc') ||
    source.includes('adhoc')
  );
}

/** Status for badges/labels — normalizes client ad-hoc drafts that already have body content. */
export function resolveOutboxDisplayStatus(draft) {
  let status = resolveOutboxStepStatus(draft);
  if (status === 'draft') return 'draft_ready';
  if (isOutboxGeneratingStatus(status) && hasOutboxMessageBody(draft) && isAdHocOutboxDraft(draft)) {
    return 'draft_ready';
  }
  return status;
}

/** True while body is still being generated (pending, processing, etc.). */
export function isOutboxGeneratingStatus(status) {
  const s = String(status || '').toLowerCase().trim();
  if (!s) return false;
  return !STABLE_STATUSES.has(s);
}

function parseIsoDate(value) {
  if (value == null || value === '') return null;
  try {
    const date = typeof value === 'string' ? parseISO(value) : new Date(value);
    return Number.isFinite(date.getTime()) ? date : null;
  } catch {
    return null;
  }
}

/** Canonical due timestamp from BE (sequence_steps.due_at). */
export function getOutboxDueAt(draft) {
  const dateStr = draft?.dueAt ?? draft?.due_at;
  return parseIsoDate(dateStr);
}

/** Tentative / planned send time (not sent_at). */
export function getOutboxSuggestedSendDate(draft) {
  const dateStr =
    draft?.scheduled_send_at ??
    draft?.scheduledSendAt ??
    draft?.dueAt ??
    draft?.due_at ??
    draft?.estimated_send;
  return parseIsoDate(dateStr);
}

export function isAdHocScheduledOutboxDraft(draft) {
  const seq = String(
    draft?.sequence_id ?? draft?.sequenceId ?? draft?.sequence?.id ?? '',
  ).trim();
  if (seq === AD_HOC_SCHEDULED_SEQUENCE_ID) return true;
  if (draft?.is_scheduled_ad_hoc === true || draft?.isScheduledAdHoc === true) return true;
  return Boolean(draft?.is_ad_hoc || draft?.isAdHoc) && resolveOutboxDisplayStatus(draft) === 'scheduled';
}

/** Stable group key for ad-hoc scheduled rows — never use persona/account ids. */
export function getOutboxCampaignKey(draft) {
  if (isAdHocScheduledOutboxDraft(draft)) return AD_HOC_SCHEDULED_SEQUENCE_ID;

  const sequence = draft?.sequence || {};
  const playbookId = sequence?.playbook_id || draft?.playbook_id || null;
  const eventId = sequence?.event_id || draft?.event_id || null;
  return (
    playbookId ||
    eventId ||
    sequence?.id ||
    draft?.sequence_id ||
    draft?.sequenceId ||
    draft?.stepId ||
    draft?.id ||
    'unknown_campaign'
  );
}

export function dedupeOutboxDraftsByStepId(drafts) {
  const seen = new Set();
  const out = [];
  for (const draft of Array.isArray(drafts) ? drafts : []) {
    const id = String(draft?.stepId ?? draft?.step_id ?? draft?.id ?? '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(draft);
  }
  return out;
}

export function getOutboxScheduledSendTimestamp(draft) {
  const raw =
    draft?.scheduled_send_at ??
    draft?.scheduledSendAt ??
    draft?.scheduled_at ??
    draft?.scheduledAt ??
    null;
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
}

function formatScheduledUtcLabel(date) {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getUTCDate();
  const month = months[date.getUTCMonth()];
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${month} ${day}, ${hours}:${minutes} UTC`;
}

/** Relative or absolute label for ad-hoc scheduled rows (never uses createdAt). */
export function formatAdHocScheduledSendLabel(date) {
  if (!(date instanceof Date) || !Number.isFinite(date.getTime())) return null;
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  if (diffMs < 0) return formatScheduledUtcLabel(date);
  const diffMins = Math.round(diffMs / 60000);
  if (diffMins < 60) return `in ${Math.max(1, diffMins)}m`;
  const diffHours = Math.round(diffMs / 3600000);
  if (diffHours < 24) return `in ${diffHours}h`;
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (date.toLocaleDateString('en-CA') === tomorrow.toLocaleDateString('en-CA')) {
    return `tomorrow ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  }
  return formatScheduledUtcLabel(date);
}

export function formatAdHocScheduledSendDisplay(date) {
  const label = formatAdHocScheduledSendLabel(date);
  return label ? `⏰ Scheduled for ${label}` : null;
}

/** Local calendar day key (YYYY-MM-DD) for due-date comparisons. */
export function outboxLocalDateKey(date) {
  if (!(date instanceof Date) || !Number.isFinite(date.getTime())) return null;
  return date.toLocaleDateString('en-CA');
}

/**
 * Client-side due-date filter — compares calendar days in the browser's local timezone.
 * Uses due_at only so null due_at is never treated as epoch / infinitely overdue.
 */
export function matchesOutboxDateFilter(draft, dateFilter) {
  const filter = String(dateFilter || 'all').trim();
  if (!filter || filter === 'all') return true;

  const dueDate = isAdHocScheduledOutboxDraft(draft)
    ? getOutboxSuggestedSendDate(draft)
    : getOutboxDueAt(draft);

  if (filter === 'no_date') return dueDate == null;
  if (dueDate == null) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueKey = outboxLocalDateKey(dueDate);
  const todayKey = outboxLocalDateKey(today);
  if (!dueKey || !todayKey) return false;

  if (filter === 'today') return dueKey === todayKey;

  if (filter === 'tomorrow') {
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return dueKey === outboxLocalDateKey(tomorrow);
  }

  if (filter === 'next_7_days') {
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);
    const nextWeekKey = outboxLocalDateKey(nextWeek);
    return dueKey >= todayKey && dueKey < nextWeekKey;
  }

  if (filter === 'overdue') return dueKey < todayKey;

  return true;
}

/** True when draft has a valid due_at before today's local calendar day. */
export function isOutboxDraftOverdue(draft, now = new Date()) {
  const dueDate = getOutboxDueAt(draft);
  if (dueDate == null) return false;
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const dueKey = outboxLocalDateKey(dueDate);
  const todayKey = outboxLocalDateKey(today);
  if (!dueKey || !todayKey) return false;
  return dueKey < todayKey;
}

export function getOutboxSentDate(draft) {
  const dateStr =
    draft?.sent_at ??
    draft?.sentAt ??
    draft?.completed_at ??
    draft?.completedAt;
  return parseIsoDate(dateStr);
}

export function getOutboxStatusBadge(status) {
  const s = String(status || '').toLowerCase().trim();

  switch (s) {
    case 'scheduled':
      return { label: 'Scheduled', className: 'bg-blue-50 text-blue-700 border-blue-200' };
    case 'draft_ready':
      return { label: 'Draft', className: 'bg-gray-50 text-gray-700 border-gray-200' };
    case 'pending':
      return {
        label: 'Generating',
        className: 'bg-amber-50 text-amber-800 border-amber-200',
        showSpinner: true,
      };
    case 'needs_review':
      return { label: 'Needs review', className: 'bg-orange-50 text-orange-800 border-orange-200' };
    case 'sent':
      return { label: 'Sent', className: 'bg-green-50 text-green-700 border-green-200' };
    case 'replied':
      return {
        label: 'Replied',
        className: 'bg-green-100 text-green-800 border-green-300 font-semibold',
      };
    case 'bounced':
      return { label: 'Bounced', className: 'bg-red-50 text-red-700 border-red-200' };
    case 'cancelled':
      return {
        label: 'Cancelled',
        className: 'bg-gray-50 text-gray-500 border-gray-200 line-through',
      };
    default:
      if (isOutboxGeneratingStatus(s)) {
        return {
          label: 'Generating',
          className: 'bg-amber-50 text-amber-800 border-amber-200',
          showSpinner: true,
        };
      }
      return null;
  }
}

export function getOutboxStatusSubtext(status, draft = null) {
  const s = String(status || '').toLowerCase().trim();

  switch (s) {
    case 'scheduled':
      return 'Will send automatically';
    case 'draft_ready':
      return 'Awaiting your approval';
    case 'pending':
      return 'Body still being written';
    case 'needs_review':
      return 'QA blocked — open to inspect';
    case 'bounced':
      return draft?.bounce_reason || draft?.bounceReason || null;
    default:
      if (isOutboxGeneratingStatus(s)) return 'Body still being written';
      return null;
  }
}

/**
 * Date line for list rows and drawer metadata.
 * "Scheduled" wording only when status === 'scheduled'.
 */
export function formatOutboxDateLine(draft) {
  const status = resolveOutboxDisplayStatus(draft);

  if (status === 'scheduled') {
    const date = getOutboxSuggestedSendDate(draft);
    if (!date) return null;
    if (isAdHocScheduledOutboxDraft(draft)) {
      const adHocLabel = formatAdHocScheduledSendLabel(date);
      if (!adHocLabel) return null;
      return {
        prefix: '⏰ Scheduled for',
        dateText: adHocLabel,
        className: 'text-blue-700 font-medium',
        italic: false,
      };
    }
    return {
      prefix: 'Sends',
      dateText: format(date, 'MMM d, h:mm a'),
      className: 'text-[#5C6B7A]',
      italic: false,
    };
  }

  if (status === 'draft_ready' || status === 'needs_review') {
    const date = getOutboxSuggestedSendDate(draft);
    if (!date) return null;
    return {
      prefix: 'Suggested',
      dateText: format(date, 'MMM d, h:mm a'),
      className: 'text-gray-500',
      italic: true,
    };
  }

  if (status === 'pending' || isOutboxGeneratingStatus(status)) {
    return null;
  }

  if (status === 'sent' || status === 'replied' || status === 'bounced') {
    const date = getOutboxSentDate(draft);
    if (!date) return null;
    return {
      prefix: 'Sent',
      dateText: format(date, 'MMM d, h:mm a'),
      className: 'text-[#5C6B7A]',
      italic: false,
    };
  }

  return null;
}

/** Prominent schedule banner in the drawer — blue only for status === 'scheduled'. */
export function getOutboxScheduleBanner(draft) {
  const status = resolveOutboxDisplayStatus(draft);
  const dateLine = formatOutboxDateLine(draft);
  if (!dateLine) return null;

  if (status === 'scheduled') {
    return {
      containerClass: 'bg-blue-50 px-3 py-1.5 rounded-md border border-blue-200',
      iconClass: 'text-blue-600',
      label: 'Sends',
      labelClass: 'text-xs text-blue-600 font-medium',
      dateClass: 'text-sm text-blue-800 ml-2 font-semibold',
      dateText: dateLine.dateText,
      subtext: getOutboxStatusSubtext(status),
    };
  }

  if (status === 'draft_ready' || status === 'needs_review') {
    return {
      containerClass: 'bg-gray-50 px-3 py-1.5 rounded-md border border-gray-200',
      iconClass: 'text-gray-500',
      label: 'Suggested',
      labelClass: 'text-xs text-gray-500 font-medium italic',
      dateClass: 'text-sm text-gray-700 ml-2 font-medium italic',
      dateText: dateLine.dateText,
      subtext: getOutboxStatusSubtext(status),
    };
  }

  return null;
}
